import { HTTP_INTERCEPTORS, HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import { Injector, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { TRANSLOCO_LOADING_TEMPLATE } from '@ngneat/transloco';

import { DialogService } from 'primeng/dynamicdialog';

import { CoreModule } from '@core/core.module';
import { HttpAuthInterceptor, HttpErrorInterceptor, HttpTimeoutInterceptor, HttpXsrfInterceptor } from '@core/http';
import { LayoutModule } from '@core/layout/layout.module';
import { PipeModule } from '@core/pipe/pipe.module';
import { BroadcastService, ExportEffects, FileService, GridEffects, ListEffects, LoggerService, ServiceLocator } from '@core/services';
import { SharedModule } from '@core/shared.module';
import { metaReducers, reducers } from '@core/state';
import { TranslocoRootModule, preload } from '@util/translate';

import { settings } from '@environment';

import { AdminEffects } from '@admin/state';
import { AuthModule } from '@auth/auth.module';
import { AuthService } from '@auth/services';
import { AuthEffects } from '@auth/state';
import { CarrierModule } from '@carrier/carrier.module';
import { CarrierEffects } from '@carrier/state';
import { ConsigneeModule } from '@consignee/consignee.module';
import { ConsigneeEffects } from '@consignee/state';
import { CustomerModule } from '@customer/customer.module';
import { CustomerEffects } from '@customer/state';
import { FacilityModule } from '@facility/facility.module';
import { FacilityEffects } from '@facility/state';
import { InventoryModule } from '@inventory/inventory.module';
import { InventoryEffects } from '@inventory/state';
import { ItemModule } from '@item/item.module';
import { ItemEffects } from '@item/state';
import { OrderEffects } from '@order/state';
import { ReportEffects } from '@report/state';
import { ShipmentModule } from '@shipment/shipment.module';
import { ShipmentEffects } from '@shipment/state';
import { SystemEffects } from '@system/state';
import { SystemModule } from '@system/system.module';
import { UserEffects } from '@user/state';

import { AppComponent } from './app.component';

import { AppRoutingModule } from './app-routing.module';

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    TranslocoRootModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    AuthModule,
    CarrierModule,
    ConsigneeModule,
    CoreModule,
    CustomerModule,
    FacilityModule,
    InventoryModule,
    ItemModule,
    LayoutModule,
    PipeModule,
    SharedModule,
    ShipmentModule,
    SystemModule,
    StoreModule.forRoot(reducers, {
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
        strictStateSerializability: false,
        strictActionSerializability: false,
        strictActionWithinNgZone: false,
        strictActionTypeUniqueness: false,
      },
      metaReducers,
    }),
    EffectsModule.forRoot([
      AuthEffects,
      AdminEffects,
      CarrierEffects,
      ConsigneeEffects,
      CustomerEffects,
      ExportEffects,
      FacilityEffects,
      GridEffects,
      InventoryEffects,
      ItemEffects,
      ListEffects,
      OrderEffects,
      ReportEffects,
      ShipmentEffects,
      SystemEffects,
      UserEffects,
    ]),
    StoreDevtoolsModule.instrument({
      name: 'Synapse Anywhere',
      logOnly: true,
      maxAge: 250,
      predicate: (_state, action) => {
        if (action.type.includes('Export Success')) {
          return false;
        }

        //if (action.type.match(/\[(Grid|Load).*/gi)) {
        //  return true;
        //} else {
        //  return false;
        //}

        return true;
      },
    }),
    HttpClientModule,
    HttpClientXsrfModule.withOptions({
      cookieName: settings.xsrfTokenCookie,
      headerName: settings.xsrfTokenHeader,
    }),
    // disabled until service workers on the same domain is supported
    // ServiceWorkerModule.register('ngsw-worker.js', {
    //   enabled: environment.production,
    //   registrationStrategy: 'registerWithDelay:5000',
    // }),
  ],
  providers: [
    preload,
    AuthService,
    BroadcastService,
    DialogService,
    LoggerService,
    FileService,
    { provide: HTTP_INTERCEPTORS, useClass: HttpTimeoutInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpXsrfInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpAuthInterceptor, multi: true },
    {
      provide: TRANSLOCO_LOADING_TEMPLATE,
      useValue: '<p>loading...</p>',
    },
  ],
  exports: [SharedModule],
})
export class AppModule {
  constructor(injector: Injector) {
    const injectorListener = ServiceLocator.observableInjector$.subscribe((observableInjector) => {
      ServiceLocator.injector = observableInjector;

      if (ServiceLocator.injector) {
        injectorListener.unsubscribe();
      }
    });

    ServiceLocator.observableInjector$.next(injector);
  }
}
